import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  LinearProgress,
  TableContainer,
  TableSortLabel
} from '@material-ui/core';
import {
  Assessment as AssessmentIcon,
  Close as CloseIcon
} from '@material-ui/icons';

import { ApiParams } from '../../../common/api/api';
import CardTitle from '../../../common/card/CardTitle';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import WrappedTableCell from '../../../common/WrappedTableCell';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { ProductBoardColumnEntity } from '../board/ProductBoardColumn';
import DesignOutputAssignVerificationDialog from '../dialogs/DesignOutputAssignVerificationDialog';
import UnlinkConfirmDialog from '../dialogs/UnlinkConfirmDialog';
import {
  useFindProductVerificationsByDesignOutputId,
  useProductEntityPrefix
} from '../Product.hooks';
import {
  ProductVerification,
  BaseProductDesignOutputCardProps
} from '../Product.interface';
import { getProductEntityName } from '../Product.config';

const DesignOutputVerificationReferenceCard: React.FC<BaseProductDesignOutputCardProps> =
  (props: BaseProductDesignOutputCardProps) => {
    const {
      productId,
      productDesignOutput,
      productDesignOutputVersion,
      onUpdate,
      onError
    } = props;
    const history = useHistory();
    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const [itemToUnlink, setItemToUnlink] = useState<number | undefined>();
    const [order, setOrder] = useState<string | undefined>();
    const [orderDirection, setOrderDirection] = useState<
      'ASC' | 'DESC' | undefined
    >();
    const {
      productVerificationList,
      isLoadingProductVerifications,
      loadingProductVerificationsError,
      findProductVerifications
    } = useFindProductVerificationsByDesignOutputId(
      productId,
      productDesignOutput.id
    );
    const { isFieldDisabled } = useTaskEntityFields(productDesignOutput.task);
    const prefix: string = useProductEntityPrefix(
      ProductBoardColumnEntity.Verification
    );

    const handleSort = (column: string) => {
      setOrder(column);
      setOrderDirection(orderDirection === 'ASC' ? 'DESC' : 'ASC');
    };

    const getSortDirection = (column: string): 'asc' | 'desc' => {
      return order === column && orderDirection === 'DESC' ? 'desc' : 'asc';
    };

    const findProductVerificationsWithParams = useCallback(() => {
      const params: ApiParams = {};
      if (order) params.order = order;
      if (orderDirection) params.direction = orderDirection;
      if (productDesignOutputVersion)
        params.version = productDesignOutputVersion.version;
      findProductVerifications(params);
    }, [
      findProductVerifications,
      order,
      orderDirection,
      productDesignOutputVersion
    ]);

    useEffect(() => {
      findProductVerificationsWithParams();
    }, [findProductVerificationsWithParams]);

    useEffect(() => {
      if (loadingProductVerificationsError) {
        onError?.(loadingProductVerificationsError);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingProductVerificationsError]);
    const disabled =
      Boolean(productDesignOutputVersion) ||
      isFieldDisabled('assignVerification');
    return (
      <Card>
        <CardTitle>
          <AssessmentIcon />
          {getProductEntityName('verification')} Reference
        </CardTitle>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={order === 'productVerification.id'}
                    direction={getSortDirection('productVerification.id')}
                    onClick={() => handleSort('productVerification.id')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'productVerification.title'}
                    direction={getSortDirection('productVerification.title')}
                    onClick={() => handleSort('productVerification.title')}
                  >
                    Title
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'task.status'}
                    direction={getSortDirection('task.status')}
                    onClick={() => handleSort('task.status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingProductVerifications && (
                <TableRow>
                  <TableCell colSpan={2} padding='none'>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              )}
              {productVerificationList?.map(
                (verification: ProductVerification) => (
                  <TableRow
                    hover
                    key={verification.id}
                    onClick={() => {
                      history.push(
                        `/portal/products/${productId}/verifications/${verification.id}`
                      );
                    }}
                  >
                    <TableCell>{prefix + verification.id}</TableCell>
                    <WrappedTableCell>{verification.title}</WrappedTableCell>
                    <TableCell>
                      <TaskStatusLabel task={verification.task} />
                    </TableCell>
                    <TableCell
                      style={{
                        textAlign: 'center'
                      }}
                    >
                      {!disabled && (
                        <CloseIcon
                          onClick={event => {
                            event.stopPropagation();
                            setItemToUnlink(verification.id);
                          }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                )
              )}
              <TableRow>
                <TableCell colSpan={2}>
                  <Button
                    variant='outlined'
                    color='primary'
                    disabled={disabled}
                    onClick={() => setOpenDialog(true)}
                  >
                    Assign Verifications
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        {openDialog && (
          <DesignOutputAssignVerificationDialog
            open={openDialog}
            productId={productId}
            designOutputId={productDesignOutput.id}
            selectedItems={productVerificationList}
            onError={onError}
            onCancel={() => setOpenDialog(false)}
            onAssigned={() => {
              findProductVerificationsWithParams();
              onUpdate?.();
              setOpenDialog(false);
            }}
          />
        )}
        <UnlinkConfirmDialog
          open={!!itemToUnlink}
          productId={productId}
          entityId={productDesignOutput.id}
          from={ProductBoardColumnEntity.DesignOutput}
          to={ProductBoardColumnEntity.Verification}
          unlinkItemId={itemToUnlink}
          selectedItems={productVerificationList}
          onError={onError}
          onCancel={() => setItemToUnlink(undefined)}
          onChange={() => {
            findProductVerificationsWithParams();
            onUpdate?.();
            setItemToUnlink(undefined);
          }}
        />
      </Card>
    );
  };

export default DesignOutputVerificationReferenceCard;
